/**
 * DataEntryPage — Focused data entry with topic cards
 *
 * Phase 3 of the adaptive onboarding flow. Shows 2-4 archetype-based
 * topic cards that expand into mini-forms with 3-8 settings fields each.
 * Cards are selected based on the user's IntakeProfile.
 *
 * Input values are tracked in local state and persisted to Firestore
 * settings on blur and before navigation. Fields are grouped by their
 * settings category so each updateSettings call targets the correct
 * Firestore document.
 */

import { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { cn } from "../../utils/cn";
import { useAuth } from "../../hooks/useAuth";
import { getIntakeProfile } from "../../services/intakeService";
import { getTopicCardsForProfile } from "../../services/topicCardService";
import { updateSettings } from "../../services/settingsService";
import type {
  TopicCard,
  TopicCardField,
} from "../../services/topicCardService";
import type { IntakeProfile } from "../../types/intake";
import type { SettingsCategory } from "../../types/settings";
import { createLogger } from "../../utils/logger";

const dataEntryLogger = createLogger({ prefix: "DataEntry" });

/**
 * Default intake profile used when the real profile is unavailable.
 * Maps to the "Early Career Renter" archetype which provides
 * the most universally relevant topic cards.
 */
const DEFAULT_INTAKE_PROFILE: IntakeProfile = {
  intakeVersion: 1,
  completedAt: new Date(),
  lifeStage: "building",
  household: "solo",
  housing: "rent",
  employment: "employed-salary",
  investmentLevel: "beginner",
  debtSituation: ["debt-free"],
  nearTermEvents: ["none"],
  knowledgeLevel: "standard",
};

/** Local state for field values keyed by fieldName */
type FieldValues = Record<string, number | undefined>;

export default function DataEntryPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [cards, setCards] = useState<TopicCard[]>([]);
  const [expandedCardId, setExpandedCardId] = useState<string | null>(null);
  const [fieldValues, setFieldValues] = useState<FieldValues>({});
  const cardsRef = useRef<TopicCard[]>([]);

  useEffect(() => {
    if (!user?.userId) return;

    let cancelled = false;

    async function loadCards() {
      const result = await getIntakeProfile(user!.userId);
      if (cancelled) return;

      let profile: IntakeProfile | null = null;
      if (result.ok && result.value) {
        profile = result.value;
      }

      if (!profile) {
        dataEntryLogger.warn("No intake profile, showing default cards");
      }

      // Always show topic cards — use actual profile or a default archetype
      const selectedCards = getTopicCardsForProfile(
        profile ?? DEFAULT_INTAKE_PROFILE
      );
      setCards(selectedCards);
      cardsRef.current = selectedCards;
      setLoading(false);
    }

    loadCards();
    return () => {
      cancelled = true;
    };
  }, [user]);

  const handleCardToggle = useCallback((cardId: string) => {
    setExpandedCardId((prev) => (prev === cardId ? null : cardId));
  }, []);

  const handleFieldChange = useCallback(
    (fieldName: string, rawValue: string) => {
      const value = rawValue === "" ? undefined : Number(rawValue);
      setFieldValues((prev) => ({ ...prev, [fieldName]: value }));
    },
    []
  );

  /** Save a single field to its settings category in Firestore */
  const saveField = async (
    field: TopicCardField,
    value: number | undefined
  ) => {
    if (!user?.userId || value === undefined) return;
    const category = field.category as SettingsCategory;
    await updateSettings(user.userId, category, {
      [field.fieldName]: value,
    });
  };

  const handleFieldBlur = useCallback(
    (field: TopicCardField) => {
      const value = fieldValues[field.fieldName];
      saveField(field, value);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps -- saveField reads user/updateSettings from closure
    [fieldValues]
  );

  /** Save all entered data grouped by category before navigating */
  const saveAllFields = async () => {
    if (!user?.userId) return;

    // Collect all fields with values, grouped by category
    const byCat: Record<string, Record<string, number>> = {};
    const allFields = cardsRef.current.flatMap((c) => c.fields);

    for (const field of allFields) {
      const value = fieldValues[field.fieldName];
      if (value !== undefined) {
        const cat = field.category;
        if (!byCat[cat]) byCat[cat] = {};
        byCat[cat][field.fieldName] = value;
      }
    }

    // Save each category in parallel
    const saves = Object.entries(byCat).map(([category, data]) =>
      updateSettings(user.userId, category as SettingsCategory, data)
    );

    await Promise.all(saves);
  };

  const handleDone = async () => {
    await saveAllFields();
    navigate("/onboarding/celebration", { replace: true });
  };

  if (loading) {
    return (
      <div className="data-entry-page" id="data-entry">
        <div className="data-entry-loading">Loading your topics...</div>
      </div>
    );
  }

  return (
    <div className="data-entry-page" id="data-entry">
      <h1 className="data-entry-header" id="data-entry-header">
        Let&apos;s fill in the details
      </h1>
      <p className="data-entry-subtitle">
        Fill in what you can — you can always update these later in Settings.
      </p>

      {/* Topic Cards */}
      <div className="data-entry-cards" id="data-entry-cards">
        {cards.map((card) => {
          const isExpanded = expandedCardId === card.id;
          return (
            <div
              key={card.id}
              className={cn(
                "data-entry-card",
                isExpanded && "data-entry-card-expanded"
              )}
              id={`card-${card.id}`}
            >
              <button
                type="button"
                className="data-entry-card-header"
                onClick={() => handleCardToggle(card.id)}
                aria-expanded={isExpanded}
              >
                <span className="data-entry-card-icon">{card.icon}</span>
                <span className="data-entry-card-title">{card.title}</span>
              </button>

              {isExpanded && (
                <div className="data-entry-card-fields">
                  {card.fields.map((field) => (
                    <div key={field.fieldName} className="data-entry-field">
                      <label
                        className="data-entry-field-label"
                        htmlFor={`field-${field.fieldName}`}
                      >
                        {field.label}
                      </label>
                      <input
                        type="number"
                        id={`field-${field.fieldName}`}
                        className="data-entry-field-input"
                        placeholder="0"
                        value={fieldValues[field.fieldName] ?? ""}
                        onChange={(e) =>
                          handleFieldChange(field.fieldName, e.target.value)
                        }
                        onBlur={() => handleFieldBlur(field)}
                      />
                    </div>
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Done Button */}
      <div className="data-entry-actions" id="data-entry-actions">
        <button
          type="button"
          className="data-entry-done"
          onClick={handleDone}
          aria-label="Continue to my dashboard"
        >
          Continue to my dashboard →
        </button>
      </div>
    </div>
  );
}
